// Named imports (not `import * as Sentry`) keep the surface explicit. All of
// @sentry/* is split into its own vendor chunk via `advancedChunks` in
// vite.config.ts, so none of it (including the ~120 kB Session Replay
// integration) weighs down the entry chunk.
import {
  browserProfilingIntegration,
  consoleLoggingIntegration,
  init,
  reactRouterV7BrowserTracingIntegration,
  replayIntegration,
} from "@sentry/react";
import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// Disable Sentry in local development — only initialize for real builds.
if (dsn && !import.meta.env.DEV) {
  init({
    dsn,
    environment: import.meta.env.VITE_ENV ?? import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION as string | undefined,

    sendDefaultPii: true,

    integrations: [
      // SPA navigation + page-load + fetch/xhr spans, with React Router v7 hooks
      reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      replayIntegration({ maskAllText: true, blockAllMedia: true }),
      // Forward console.* calls to Sentry Logs.
      consoleLoggingIntegration({ levels: ["log", "info", "warn", "error"] }),
      // JS Self-Profiling API (experimental). Requires the document to be served
      // with `Document-Policy: js-profiling` — otherwise this is a silent no-op.
      browserProfilingIntegration(),
    ],

    // Tracing
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/api\.mashit\.dev/,
      /^https:\/\/app\.mashit\.dev/,
    ],

    // Session Replay
    replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Profiling — fraction of sampled traces that also produce a profile.
    profilesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,

    // Logs — enables Sentry.logger.* and the consoleLoggingIntegration above.
    enableLogs: true,
  });
}
